import React, { useId } from "react";

import {
  ResilienceReport,
  ScoreTile,
} from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import { K } from "./parts";
import { formatScore } from "./resilience-view";
import styles from "./resilience.module.scss";

// Safari drops a list's role once its markers are styled away.
const LIST = "list";

// Each card is titled by what its figure is, with the figure beneath.
const Answer = ({
  title,
  tile,
  format,
}: {
  title: string;
  tile: ScoreTile;
  format: (value: number) => string;
}) => {
  const { t } = useTranslation(["admin"]);
  return (
    <li className={styles["feedback-item"]}>
      <span className={styles["feedback-title"]}>{title}</span>
      {tile.suppressed ? (
        <p className={styles["not-enough"]}>
          {t(`${K}.sessionFeedback.insufficient`)}
        </p>
      ) : (
        <strong className={styles["feedback-value"]}>
          {format(tile.value)}
        </strong>
      )}
    </li>
  );
};

const SessionFeedback = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const headingId = useId();
  const activity = report.activity;
  const percent = (value: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "percent",
      maximumFractionDigits: 0,
    }).format(value / 100);
  const outOfTen = (value: number) =>
    t(`${K}.sessionFeedback.outOfTen`, {
      score: formatScore(value, i18n.language),
    });

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className={styles["section-title"]}>
        {t(`${K}.sections.sessionFeedback`)}
      </h3>
      <p className={styles["card-description"]}>
        {t(`${K}.sessionFeedback.description`)}
      </p>
      <ul className={styles["feedback"]} role={LIST}>
        <Answer
          title={t(`${K}.sessionFeedback.lessStressedTitle`)}
          tile={activity.less_stressed_percent}
          format={percent}
        />
        <Answer
          title={t(`${K}.sessionFeedback.gainedSkillsTitle`)}
          tile={activity.gained_skills_percent}
          format={percent}
        />
        <Answer
          title={t(`${K}.sessionFeedback.recommendationTitle`)}
          tile={activity.recommendation}
          format={outOfTen}
        />
      </ul>
    </section>
  );
};

export default SessionFeedback;
